<template>
    <div class="card border-light rounded-3 shadow-lg">
        <div class="card-body">
            <h2 class="card-title h4 mb-4 text-primary text-center text-md-start">Authentification</h2>
            <form @submit.prevent="signIn">
                <div v-if="!awaitingOtp">
                    <div class="mb-3">
                        <label for="username">Numéro Constances [8 chiffres]</label>
                        <input
                            type="text"
                            id="username"
                            v-model="num"
                            class="form-control"
                            maxlength="8"
                            pattern="[0-9]{8}"
                            placeholder="Votre identifiant à 8 chiffres"
                            required
                            @keyup="formatNum"
                        />
                    </div>

                    <div class="mb-3">
                        <label for="password">Mot de passe </label>
                        <div class="input-group">
                            <input
                                :type="showPassword ? 'text' : 'password'"
                                id="password"
                                v-model="password"
                                class="form-control"
                                maxlength="30"
                                placeholder="Votre mot de passe"
                                required
                            />
                            <span class="input-group-text" @click="togglePassword"><i :class="'bi bi-eye-' + (showPassword ? 'slash-' : '') + 'fill'"></i></span>
                        </div>
                    </div>
                    
                    <div ref="captcha" class="d-flex justify-content-center"></div>
                </div>

                <div v-else class="mb-3">
                    <label for="otp">Code de sécurité reçu par {{ alert && alert.message.endsWith('e-mail') ? 'e-mail' : 'SMS' }}</label>
                    <input
                        type="text"
                        id="otp"
                        v-model="otpCode"
                        class="form-control"
                        maxlength="6"
                        pattern="[0-9]{6}"
                        placeholder="Saisissez le code à 6 chiffres"
                        required
                    />
                    <small class="form-text text-muted">Un nouveau code sera nécessaire si vous fermez cette page ou attendez plus de quelques minutes.</small>
                </div>

                <div :class="'alert' + (alert ? ' alert-' + alert.class : '') + ' text-center p-1 mt-2 fs-n1' + (!alert ? ' d-none' : '')" role="alert" v-html="alert ? alert.message : '&nbsp;'"></div>

                <div class="mt-4 mb-2 text-center">
                    <button type="submit" class="btn btn-primary rounded-pill" :disabled="loading">
                        {{ awaitingOtp ? 'Valider le code' : 'Se connecter' }}
                    </button>
                    <button v-if="awaitingOtp" type="reset" class="btn btn-secondary rounded-pill" style="margin-left: 5px;" :disabled="loading" @click="resend_code">
                        Renvoyer le code
                    </button>
                </div>

                <div class="text-center" v-if="!awaitingOtp">
                    <input type="checkbox" id="stay-connected" v-model="remember" class="form-check-input" />
                    <label class="form-check-label ps-1 fw-light" for="stay-connected">Rester connecté</label>
                </div>

                <div class="mt-4 text-center" v-if="!awaitingOtp">
                    <router-link to="/mot-de-passe" class="card-link fs-n1">Mot de passe oublié</router-link>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ref, watch, onBeforeUnmount } from 'vue'
import { getCaptcha } from '../tools';

export default {
    name: 'LoginForm',
    setup() {
        const captcha = ref(null)
        const widget = ref(null)

        const captchaKey = process.env.VUE_APP_CAPTCHA_PUBLIC_KEY
        const captchaToken = ref(null)
        const loading = ref(false)

        watch(captcha, () => {
            // reset the widget instance when the container changes
            if (widget.value) {
                widget.value.destroy()
                widget.value = null
            }

            if (captcha.value) {
                widget.value = getCaptcha(captcha.value, captchaKey, captchaToken, loading)
            }
        })

        const resetCaptcha = () => {
            captchaToken.value = null
            if (widget.value) {
                widget.value.reset()
            }

            if (captcha.value) {
                widget.value = getCaptcha(captcha.value, captchaKey, captchaToken, loading)
            }
        }

        onBeforeUnmount(() => {
            if (widget.value) {
                widget.value.destroy()
            }
        })

        return {
            captcha,
            captchaKey,
            captchaToken,
            loading,
            resetCaptcha,
        }
    },
    data: () => ({
        num: '',
        password: '',
        remember: true,
        showPassword: false,
        awaitingOtp: false,
        otpCode: '',
        alert: null,
    }),
    computed: {
        ...mapGetters(['isAuth']),
    },
    methods: {
        ...mapActions(['logIn', 'confirmOtpLogin', 'resendOtp']),
        formatNum() {
            this.num = this.num.replace(/\D/, '')
        },
        togglePassword() {
            this.showPassword = !this.showPassword
        },
        async signIn() {
            this.alert = null

            if (!this.awaitingOtp) {
                if (!this.captchaToken) {
                    this.alert = { class: 'warning', message: "Veuillez confirmer que vous n'êtes pas un robot" }
                    return
                }

                this.loading = true
                const ret = await this.logIn({
                    login: this.num,
                    password: this.password,
                    remember: this.remember,
                    captcha: this.captchaToken,
                })
                this.loading = false

                if (ret && ret.otp) {
                    this.awaitingOtp = true
                    this.alert = { class: ret.class || 'success', message: ret.message || 'Un code vous a été envoyé.' }
                } else {
                    this.alert = ret
                    this.resetCaptcha()
                }
            } else {
                this.loading = true
                const ret = await this.confirmOtpLogin({
                    login: this.num,
                    code: this.otpCode,
                    remember: this.remember,
                })
                this.loading = false

                if (!ret && this.isAuth) {
                    this.$router.push('/suivi')
                } else if (ret) {
                    this.alert = ret
                }
            }
        },
        async resend_code() {
            this.loading = true
            const ret = await this.resendOtp({
                login: this.num,
                password: this.password,
                remember: this.remember,
            })
            this.loading = false

            if (ret && ret.otp) {
                this.awaitingOtp = true
                this.alert = { class: ret.class || 'success', message: ret.message || 'Un code vous a été envoyé.' }
            } else {
                this.alert = ret
            }
        }
    },
}
</script>

<style lang="scss" scoped>
.input-group-text {
    cursor: pointer;
}
</style>
